import React, { useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import { useChartDimensions } from '../hooks/useChartDimensions';
import { filterVisibleItems, useLegendToggle } from '../hooks/useLegendToggle';
import ChartLegend from './ChartLegend';

/**
 * Stacked bar chart.
 * series: [{ key, label, color }]
 */
export default function StackedBarChart({
  data,
  xKey,
  series,
  yFormat = (v) => v.toLocaleString(),
  xFormat,
  heightRatio = 0.5,
  horizontal = false,
  percent = false,
  barLabel = false,
  onBarClick,
}) {
  const { containerRef, svgRef, tooltipRef, dimensions, hideTooltip } = useChartDimensions(
    (w) => Math.min(440, Math.max(260, w * heightRatio))
  );

  const seriesKeys = useMemo(() => (series || []).map((s) => s.key), [series]);
  const seriesLabels = useMemo(
    () => Object.fromEntries((series || []).map((s) => [s.key, s.label || s.key])),
    [series]
  );
  const { isVisible, toggle, statusMessage, hiddenKeys } = useLegendToggle(seriesKeys, {
    labels: seriesLabels,
  });
  const visibleSeries = useMemo(
    () => filterVisibleItems(series, hiddenKeys),
    [series, hiddenKeys]
  );

  useEffect(() => {
    if (!data?.length || !dimensions.width || !visibleSeries.length) {
      hideTooltip();
      return;
    }
    const { width, height } = dimensions;
    const longestLabel = d3.max(data, (d) => String(d[xKey]).length) || 6;
    const margin = horizontal
      ? { top: 10, right: 30, bottom: 30, left: Math.min(220, 8 + longestLabel * 7) }
      : { top: 20, right: 20, bottom: 64, left: 70 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('viewBox', `0 0 ${width} ${height}`);
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
    const tooltip = d3.select(tooltipRef.current);

    const keys = visibleSeries.map((s) => s.key);
    let stackData = data;
    if (percent) {
      stackData = data.map((d) => {
        const total = keys.reduce((s, k) => s + (d[k] || 0), 0) || 1;
        const out = { [xKey]: d[xKey], __total: total };
        keys.forEach((k) => (out[k] = (d[k] || 0) / total));
        return out;
      });
    }
    const stack = d3.stack().keys(keys)(stackData);

    if (horizontal) {
      const y = d3.scaleBand().domain(stackData.map((d) => d[xKey])).range([0, innerH]).padding(0.22);
      const xMax = percent ? 1 : d3.max(stack[stack.length - 1], (d) => d[1]) || 1;
      const x = d3.scaleLinear().domain([0, xMax * 1.02]).range([0, innerW]).nice();

      g.append('g').call(d3.axisBottom(x).ticks(5).tickSize(innerH).tickFormat(''))
        .selectAll('line').attr('stroke', '#E2E8F0').attr('stroke-dasharray', '3,3');

      stack.forEach((layer, li) => {
        g.selectAll(`.bar-${li}`).data(layer).enter().append('rect')
          .attr('y', (d) => y(d.data[xKey]))
          .attr('height', y.bandwidth())
          .attr('x', (d) => x(d[0]))
          .attr('width', (d) => x(d[1]) - x(d[0]))
          .attr('fill', visibleSeries[li].color)
          .attr('cursor', onBarClick ? 'pointer' : 'default')
          .on('mouseenter', (event, d) => {
            const v = d.data[visibleSeries[li].key] || 0;
            tooltip.style('opacity', 1)
              .style('left', `${event.clientX + 12}px`).style('top', `${event.clientY - 10}px`)
              .html(`<div class="tip-label">${d.data[xKey]}</div><div class="tip-row"><span>${visibleSeries[li].label}</span><span class="tip-value">${percent ? (v * 100).toFixed(1) + '%' : yFormat(v)}</span></div>`);
          })
          .on('mousemove', (event) => tooltip.style('left', `${event.clientX + 12}px`).style('top', `${event.clientY - 10}px`))
          .on('mouseleave', hideTooltip)
          .on('click', (event, d) => {
            if (!onBarClick) return;
            event.stopPropagation();
            onBarClick(d.data, visibleSeries[li]);
          });
      });

      if (barLabel && !percent) {
        const topLayer = stack[stack.length - 1];
        g.selectAll('.stack-total').data(topLayer).enter().append('text')
          .attr('x', (d) => x(d[1]) + 5)
          .attr('y', (d) => y(d.data[xKey]) + y.bandwidth() / 2)
          .attr('dy', '0.35em')
          .attr('font-size', '11px')
          .attr('fill', '#475569')
          .attr('font-weight', '600')
          .text((d) => yFormat(d[1]));
      }

      g.append('g').call(d3.axisLeft(y).tickFormat(xFormat || ((d) => (String(d).length > 24 ? String(d).slice(0, 24) + '…' : d))))
        .selectAll('text').attr('font-size', '11px').attr('fill', '#475569');
      g.append('g').attr('transform', `translate(0,${innerH})`)
        .call(d3.axisBottom(x).ticks(5).tickFormat(percent ? (v) => `${(v * 100).toFixed(0)}%` : yFormat))
        .selectAll('text').attr('font-size', '11px').attr('fill', '#64748B');
    } else {
      const x = d3.scaleBand().domain(stackData.map((d) => d[xKey])).range([0, innerW]).padding(0.22);
      const yMax = percent ? 1 : d3.max(stack[stack.length - 1], (d) => d[1]) || 1;
      const y = d3.scaleLinear().domain([0, yMax * 1.02]).range([innerH, 0]).nice();

      g.append('g').call(d3.axisLeft(y).ticks(5).tickSize(-innerW).tickFormat(''))
        .selectAll('line').attr('stroke', '#E2E8F0').attr('stroke-dasharray', '3,3');

      stack.forEach((layer, li) => {
        g.selectAll(`.bar-${li}`).data(layer).enter().append('rect')
          .attr('x', (d) => x(d.data[xKey]))
          .attr('width', x.bandwidth())
          .attr('y', (d) => y(d[1]))
          .attr('height', (d) => y(d[0]) - y(d[1]))
          .attr('fill', visibleSeries[li].color)
          .attr('cursor', onBarClick ? 'pointer' : 'default')
          .on('mouseenter', (event, d) => {
            const v = d.data[visibleSeries[li].key] || 0;
            tooltip.style('opacity', 1)
              .style('left', `${event.clientX + 12}px`).style('top', `${event.clientY - 10}px`)
              .html(`<div class="tip-label">${d.data[xKey]}</div><div class="tip-row"><span>${visibleSeries[li].label}</span><span class="tip-value">${percent ? (v * 100).toFixed(1) + '%' : yFormat(v)}</span></div>`);
          })
          .on('mousemove', (event) => tooltip.style('left', `${event.clientX + 12}px`).style('top', `${event.clientY - 10}px`))
          .on('mouseleave', hideTooltip)
          .on('click', (event, d) => {
            if (!onBarClick) return;
            event.stopPropagation();
            onBarClick(d.data, visibleSeries[li]);
          });
      });

      if (barLabel && !percent) {
        const topLayer = stack[stack.length - 1];
        g.selectAll('.stack-total').data(topLayer).enter().append('text')
          .attr('x', (d) => x(d.data[xKey]) + x.bandwidth() / 2)
          .attr('y', (d) => y(d[1]) - 5)
          .attr('text-anchor', 'middle')
          .attr('font-size', '11px')
          .attr('fill', '#475569')
          .attr('font-weight', '600')
          .text((d) => yFormat(d[1]));
      }

      g.append('g').attr('transform', `translate(0,${innerH})`)
        .call(d3.axisBottom(x).tickFormat(xFormat || ((d) => d)))
        .selectAll('text').attr('font-size', '11px').attr('fill', '#64748B')
        .attr('transform', 'rotate(-32)').attr('text-anchor', 'end');
      g.append('g').call(d3.axisLeft(y).ticks(5).tickFormat(percent ? (v) => `${(v * 100).toFixed(0)}%` : yFormat))
        .selectAll('text').attr('font-size', '11px').attr('fill', '#64748B');
    }

    g.selectAll('.domain').attr('stroke', '#CBD5E1');
    g.selectAll('.tick line').attr('stroke', '#CBD5E1');
    return hideTooltip;
  }, [data, dimensions, xKey, visibleSeries, yFormat, xFormat, horizontal, percent, barLabel, onBarClick, hideTooltip, svgRef, tooltipRef]);

  if (!data?.length) return <div className="chart-container"><div className="no-data">No data</div></div>;
  return (
    <div className="chart-container" ref={containerRef}>
      <svg ref={svgRef} />
      <div className="d3-tooltip" ref={tooltipRef} style={{ opacity: 0 }} />
      <ChartLegend
        items={series}
        isVisible={isVisible}
        onToggle={toggle}
        statusMessage={statusMessage}
      />
    </div>
  );
}
